import { useEffect, useRef } from 'react';
import {
  setupPusher,
  Event,
  Subscription,
  teardownPusher,
  PusherProject
} from 'appJS/models/Pusher';
import Config from 'appJS/models/Config';

type Props = {
  pusher?: {
    config: Config;
    projects: PusherProject[];
    events: {
      name: Event;
      onUpdate: (pusherData: any) => void;
    }[];
    subscription: Subscription;
  };
  loading: boolean;
};

type PusherEvents = NonNullable<Props['pusher']>['events'];

const usePusherSubscribe = ({ pusher, loading }: Props) => {
  const latestEventsRef = useRef<PusherEvents>([]);

  useEffect(() => {
    latestEventsRef.current = pusher?.events || [];
  }, [pusher?.events]);

  useEffect(() => {
    if (!loading && pusher) {
      const { config, projects, events, subscription } = pusher;
      const subscribedEvents = events.map(({ name }) => ({
        name,
        onUpdate: (pusherData: any) => {
          const latestEvent = latestEventsRef.current.find(
            event => event.name === name
          );

          latestEvent?.onUpdate(pusherData);
        }
      }));

      setupPusher({
        config,
        events: subscribedEvents,
        subscription,
        projects
      });

      return () => {
        teardownPusher({
          config,
          projects,
          subscription
        });
      };
    }
  }, [
    loading,
    pusher?.config?.pusherApiKey,
    pusher?.config?.pusherChannelAuthEndpoint,
    pusher?.subscription,
    pusher?.projects
      ?.map(project => `${project.id}:${project.pusherChannelName}`)
      .join('|'),
    pusher?.events?.map(event => event.name).join('|')
  ]);
};

export default usePusherSubscribe;
